import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createToonGradientMap, toonMat } from './materials.js';

// ─── helpers ────────────────────────────────────────────────────────────────

function staticBox(
  world: CANNON.World,
  mat: CANNON.Material,
  hx: number,
  hy: number,
  hz: number,
  x: number,
  y: number,
  z: number,
): CANNON.Body {
  const body = new CANNON.Body({
    mass: 0,
    material: mat,
    shape: new CANNON.Box(new CANNON.Vec3(hx, hy, hz)),
  });
  body.position.set(x, y, z);
  world.addBody(body);
  return body;
}

function boxMesh(
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  parent: THREE.Group,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  parent.add(m);
  return m;
}

// ─── main class ─────────────────────────────────────────────────────────────

export class FpsPivotPlaza {
  readonly group = new THREE.Group();

  private readonly bodies: CANNON.Body[] = [];
  private readonly destructibles: THREE.Mesh[] = [];
  private readonly chestPositions: Array<{ x: number; z: number }> = [];

  private readonly grad: THREE.DataTexture;
  private readonly matConcrete: THREE.MeshToonMaterial;
  private readonly matFloor: THREE.MeshToonMaterial;
  private readonly matRoof: THREE.MeshToonMaterial;
  private readonly matGlass: THREE.MeshBasicMaterial;
  private readonly matSign: THREE.MeshStandardMaterial;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly world: CANNON.World,
    private readonly floorMat: CANNON.Material,
    private readonly objectMat: CANNON.Material,
    private readonly cx: number,
    private readonly cz: number,
  ) {
    this.grad = createToonGradientMap();

    this.matConcrete = toonMat(0xc8c0b8, this.grad);
    this.matFloor = toonMat(0x888880, this.grad);
    this.matRoof = toonMat(0x708090, this.grad);
    this.matGlass = new THREE.MeshBasicMaterial({
      color: 0x88aacc,
      transparent: true,
      opacity: 0.35,
    });
    this.matSign = new THREE.MeshStandardMaterial({
      color: 0x1a2244,
      emissive: 0x4455ff,
      emissiveIntensity: 0.6,
    });

    this.group.position.set(cx, 0, cz);
    // NOTE: FPSGame adds this.group to sceneRoot — do NOT double-add here

    this._buildAll();
  }

  // ── public API ─────────────────────────────────────────────────────────────

  getChestPositions(): Array<{ x: number; z: number }> {
    return [...this.chestPositions];
  }

  getDestructibleWalls(): THREE.Mesh[] {
    return [...this.destructibles];
  }

  update(_dt: number): void {
    // nothing dynamic yet – placeholder for future animations
  }

  dispose(): void {
    this.bodies.forEach((b) => this.world.removeBody(b));
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
    this.scene.remove(this.group);
  }

  // ── internal build ─────────────────────────────────────────────────────────

  private _buildAll(): void {
    /*
     * 2×2 grid layout (local coords centred on this.group):
     *
     *   A(-9,-9)  B(+9,-9)
     *   C(-9,+9)  D(+9,+9)
     *
     * Roads gap = ~6 m between buildings.
     */

    // Building A – office tower, 3 floors, 12×10 footprint
    this._buildingA(-9, -9);

    // Building B – corner shop, 2 floors, 8×8 footprint
    this._buildingB(10, -8);

    // Building C – apartment block, 3 floors, 14×8 footprint
    this._buildingC(-10, 10);

    // Building D – small kiosk, 1 floor, 6×6 footprint
    this._buildingD(11, 11);

    // POI banner over entrance road
    this._addBanner(0, -18);
  }

  // ── Building A: 3-floor office tower ──────────────────────────────────────

  private _buildingA(ox: number, oz: number): void {
    const W = 12, D = 10, floorH = 3, floors = 3;
    const wallT = 0.25;

    for (let f = 0; f < floors; f++) {
      const baseY = f * floorH;

      // floor slab
      const slabM = boxMesh(W, 0.3, D, this.matFloor, ox, baseY, oz, this.group);
      this._body(W / 2, 0.15, D / 2, ox, baseY, oz, this.floorMat);
      slabM.receiveShadow = true;

      // north/south exterior walls
      const northWall = boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, baseY + floorH / 2, oz - D / 2 + wallT / 2, this.group);
      this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, baseY + floorH / 2, oz - D / 2 + wallT / 2, this.objectMat);

      const southWall = boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, baseY + floorH / 2, oz + D / 2 - wallT / 2, this.group);
      this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, baseY + floorH / 2, oz + D / 2 - wallT / 2, this.objectMat);

      // east/west exterior walls
      boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox - W / 2 + wallT / 2, baseY + floorH / 2, oz, this.group);
      this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox - W / 2 + wallT / 2, baseY + floorH / 2, oz, this.objectMat);

      boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox + W / 2 - wallT / 2, baseY + floorH / 2, oz, this.group);
      this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox + W / 2 - wallT / 2, baseY + floorH / 2, oz, this.objectMat);

      // interior divider wall (destructible)
      const intWall = boxMesh(wallT, floorH - 0.3, D - 1, this.matConcrete, ox, baseY + floorH / 2, oz, this.group);
      intWall.userData.destructible = true;
      intWall.userData.wallHp = 80;
      this.destructibles.push(intWall);

      // windows in north wall
      for (let w = -1; w <= 1; w++) {
        boxMesh(1.5, 1.8, 0.1, this.matGlass, ox + w * 3.5, baseY + floorH * 0.6, oz - D / 2, this.group);
      }
      // windows in south wall
      for (let w = -1; w <= 1; w++) {
        boxMesh(1.5, 1.8, 0.1, this.matGlass, ox + w * 3.5, baseY + floorH * 0.6, oz + D / 2, this.group);
      }

      // mark south wall of top floor as destructible
      if (f === floors - 1) {
        southWall.userData.destructible = true;
        southWall.userData.wallHp = 80;
        this.destructibles.push(southWall);
      }
      void northWall; // used above
    }

    // roof slab
    const roofY = floors * floorH;
    boxMesh(W, 0.3, D, this.matRoof, ox, roofY, oz, this.group);
    this._body(W / 2, 0.15, D / 2, ox, roofY, oz, this.floorMat);

    // roof lip walls
    boxMesh(W, 0.5, wallT, this.matRoof, ox, roofY + 0.25, oz - D / 2, this.group);
    boxMesh(W, 0.5, wallT, this.matRoof, ox, roofY + 0.25, oz + D / 2, this.group);
    boxMesh(wallT, 0.5, D, this.matRoof, ox - W / 2, roofY + 0.25, oz, this.group);
    boxMesh(wallT, 0.5, D, this.matRoof, ox + W / 2, roofY + 0.25, oz, this.group);

    // stairwell (inside, east corner, stepping from floor 0 to floor 2)
    this._addStairs(ox + W / 2 - 2.5, oz + D / 2 - 2, 0, floors - 1, floorH);

    // chest on rooftop
    this.chestPositions.push({ x: this.cx + ox - 3, z: this.cz + oz - 3 });
    this.chestPositions.push({ x: this.cx + ox + 3, z: this.cz + oz + 3 });
    // chest on floor 1 inside
    this.chestPositions.push({ x: this.cx + ox - 2, z: this.cz + oz });
  }

  // ── Building B: 2-floor corner shop ────────────────────────────────────────

  private _buildingB(ox: number, oz: number): void {
    const W = 8, D = 8, floorH = 3, floors = 2;
    const wallT = 0.25;

    for (let f = 0; f < floors; f++) {
      const baseY = f * floorH;

      boxMesh(W, 0.3, D, this.matFloor, ox, baseY, oz, this.group);
      this._body(W / 2, 0.15, D / 2, ox, baseY, oz, this.floorMat);

      // four walls
      boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, baseY + floorH / 2, oz - D / 2, this.group);
      this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, baseY + floorH / 2, oz - D / 2, this.objectMat);

      const sWall = boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, baseY + floorH / 2, oz + D / 2, this.group);
      this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, baseY + floorH / 2, oz + D / 2, this.objectMat);
      sWall.userData.destructible = true;
      sWall.userData.wallHp = 80;
      this.destructibles.push(sWall);

      boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox - W / 2, baseY + floorH / 2, oz, this.group);
      this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox - W / 2, baseY + floorH / 2, oz, this.objectMat);

      boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox + W / 2, baseY + floorH / 2, oz, this.group);
      this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox + W / 2, baseY + floorH / 2, oz, this.objectMat);

      // windows
      boxMesh(1.5, 1.8, 0.1, this.matGlass, ox - 2, baseY + floorH * 0.6, oz - D / 2, this.group);
      boxMesh(1.5, 1.8, 0.1, this.matGlass, ox + 2, baseY + floorH * 0.6, oz - D / 2, this.group);
    }

    // roof
    const roofY = floors * floorH;
    boxMesh(W, 0.3, D, this.matRoof, ox, roofY, oz, this.group);
    this._body(W / 2, 0.15, D / 2, ox, roofY, oz, this.floorMat);

    this._addStairs(ox + W / 2 - 2, oz - D / 2 + 2, 0, floors - 1, floorH);

    this.chestPositions.push({ x: this.cx + ox, z: this.cz + oz + 2 });
    this.chestPositions.push({ x: this.cx + ox - 2, z: this.cz + oz - 2 });
  }

  // ── Building C: 3-floor apartment block with balconies ────────────────────

  private _buildingC(ox: number, oz: number): void {
    const W = 14, D = 8, floorH = 3, floors = 3;
    const wallT = 0.25;

    for (let f = 0; f < floors; f++) {
      const baseY = f * floorH;

      boxMesh(W, 0.3, D, this.matFloor, ox, baseY, oz, this.group);
      this._body(W / 2, 0.15, D / 2, ox, baseY, oz, this.floorMat);

      // exterior walls
      boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, baseY + floorH / 2, oz - D / 2, this.group);
      this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, baseY + floorH / 2, oz - D / 2, this.objectMat);

      boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, baseY + floorH / 2, oz + D / 2, this.group);
      this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, baseY + floorH / 2, oz + D / 2, this.objectMat);

      boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox - W / 2, baseY + floorH / 2, oz, this.group);
      this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox - W / 2, baseY + floorH / 2, oz, this.objectMat);

      boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox + W / 2, baseY + floorH / 2, oz, this.group);
      this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox + W / 2, baseY + floorH / 2, oz, this.objectMat);

      // interior divider
      const intWall = boxMesh(wallT, floorH - 0.3, D - 1, this.matConcrete, ox + 2, baseY + floorH / 2, oz, this.group);
      intWall.userData.destructible = true;
      intWall.userData.wallHp = 80;
      this.destructibles.push(intWall);

      // windows on north & south
      for (let w = -2; w <= 2; w++) {
        boxMesh(1.5, 1.8, 0.1, this.matGlass, ox + w * 2.8, baseY + floorH * 0.6, oz - D / 2, this.group);
      }

      // balcony on south side (floors 1+)
      if (f >= 1) {
        const balY = baseY;
        boxMesh(5, 0.2, 1.5, this.matFloor, ox, balY, oz + D / 2 + 0.75, this.group);
        this._body(2.5, 0.1, 0.75, ox, balY, oz + D / 2 + 0.75, this.floorMat);
        // balcony railing
        boxMesh(5, 0.8, 0.1, this.matConcrete, ox, balY + 0.5, oz + D / 2 + 1.45, this.group);
      }
    }

    // roof
    const roofY = floors * floorH;
    boxMesh(W, 0.3, D, this.matRoof, ox, roofY, oz, this.group);
    this._body(W / 2, 0.15, D / 2, ox, roofY, oz, this.floorMat);

    // roof lips
    boxMesh(W, 0.5, wallT, this.matRoof, ox, roofY + 0.25, oz - D / 2, this.group);
    boxMesh(W, 0.5, wallT, this.matRoof, ox, roofY + 0.25, oz + D / 2, this.group);

    this._addStairs(ox - W / 2 + 2, oz - D / 2 + 2, 0, floors - 1, floorH);

    // chest on rooftop
    this.chestPositions.push({ x: this.cx + ox - 4, z: this.cz + oz });
    // chest on floor 1
    this.chestPositions.push({ x: this.cx + ox + 4, z: this.cz + oz });
  }

  // ── Building D: 1-floor kiosk ─────────────────────────────────────────────

  private _buildingD(ox: number, oz: number): void {
    const W = 6, D = 6, floorH = 3.5;
    const wallT = 0.25;

    // floor slab
    boxMesh(W, 0.3, D, this.matFloor, ox, 0, oz, this.group);
    this._body(W / 2, 0.15, D / 2, ox, 0, oz, this.floorMat);

    // walls
    boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, floorH / 2, oz - D / 2, this.group);
    this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, floorH / 2, oz - D / 2, this.objectMat);

    const bWall = boxMesh(W, floorH - 0.3, wallT, this.matConcrete, ox, floorH / 2, oz + D / 2, this.group);
    bWall.userData.destructible = true;
    bWall.userData.wallHp = 80;
    this.destructibles.push(bWall);
    this._body(W / 2, (floorH - 0.3) / 2, wallT / 2, ox, floorH / 2, oz + D / 2, this.objectMat);

    boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox - W / 2, floorH / 2, oz, this.group);
    this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox - W / 2, floorH / 2, oz, this.objectMat);

    boxMesh(wallT, floorH - 0.3, D, this.matConcrete, ox + W / 2, floorH / 2, oz, this.group);
    this._body(wallT / 2, (floorH - 0.3) / 2, D / 2, ox + W / 2, floorH / 2, oz, this.objectMat);

    // windows
    boxMesh(1.5, 1.8, 0.1, this.matGlass, ox, floorH * 0.6, oz - D / 2, this.group);
    boxMesh(1.5, 1.8, 0.1, this.matGlass, ox, floorH * 0.6, oz + D / 2, this.group);

    // roof
    boxMesh(W, 0.3, D, this.matRoof, ox, floorH, oz, this.group);
    this._body(W / 2, 0.15, D / 2, ox, floorH, oz, this.floorMat);

    this.chestPositions.push({ x: this.cx + ox, z: this.cz + oz });
  }

  // ── Stair helper ──────────────────────────────────────────────────────────

  private _addStairs(
    startX: number,
    startZ: number,
    fromFloor: number,
    toFloor: number,
    floorH: number,
  ): void {
    const stepsPerFloor = 8;
    const totalFloors = toFloor - fromFloor;
    const totalSteps = totalFloors * stepsPerFloor;
    const stepH = (totalFloors * floorH) / totalSteps;
    const stepD = 1.2;

    for (let i = 0; i < totalSteps; i++) {
      const y = fromFloor * floorH + stepH * (i + 0.5);
      const z = startZ + i * (stepD / totalSteps) * 6;
      boxMesh(1.5, stepH, stepD, this.matFloor, startX, y, z, this.group);
    }
  }

  // ── POI banner ────────────────────────────────────────────────────────────

  private _addBanner(ox: number, oz: number): void {
    const banner = new THREE.Mesh(
      new THREE.BoxGeometry(8, 1.5, 0.1),
      this.matSign,
    );
    banner.position.set(ox, 4, oz);
    banner.castShadow = false;
    this.group.add(banner);

    // two support posts
    for (const sx of [-3.5, 3.5]) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.2, 4.5, 0.2),
        toonMat(0x444444, this.grad),
      );
      post.position.set(ox + sx, 2.25, oz);
      this.group.add(post);
    }
  }

  // ── Physics util ─────────────────────────────────────────────────────────

  private _body(
    hx: number,
    hy: number,
    hz: number,
    x: number,
    y: number,
    z: number,
    mat: CANNON.Material,
  ): CANNON.Body {
    // positions are in local group space; group is offset by cx/cz
    const body = staticBox(
      this.world,
      mat,
      hx, hy, hz,
      this.cx + x,
      y,
      this.cz + z,
    );
    this.bodies.push(body);
    return body;
  }
}
